
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useAuth0 } from '@auth0/auth0-react';

import { Grid, Tabs, Tab, Box, Card, Switch, Divider } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';

import { useSoftUIController } from "hooks/context";

import { useQuery } from '@tanstack/react-query';

import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

import SoftButton from "components/SoftComponents/SoftButton";
import StatsPieChart from "components/Charts/StatisticCards/StatsPieChart";
import UptimeDoughnutChart from "components/Charts/StatisticCards/UptimeDoughnutChart";
import { Link, useNavigate } from "react-router-dom";
import SoftBadgeDot from "components/SoftComponents/SoftBadgeDot";
import { exploreContentStyle } from "../styles/index";

import groupAPI from "services/api/groups";
import deviceAPI from "services/api/bks";
import GroupLiveView from "components/Groups/GroupLiveView";
import { processDevicesData } from "utils/device";
import { processGroupData } from "utils/group";

import EventsBar from "modules/clearways/views/EventsBar";
import { sidebarWrapper } from "utils/wrapper";
import { getNamingConventions } from "utils/formatting";
import InspectionLiveviewCard from "modules/inspections/components/InspectionLiveviewCard";
import CameraLatest from "components/Camera/CameraLatest";
import LiveView from "components/LiveView";
import HealthStatusCard from "components/Cards/HealthStatusCard";
import StatsCounterCard from "components/Cards/StatsCounterCard";
import DeviceStatsCard from "components/Cards/DeviceStatsCard";
import { getConnectivityIcon } from "utils/device";
import { getDeviceTypeIcon } from "utils/device";
import { Close } from "@mui/icons-material";
import MapLinkButton from "components/Buttons/MapLinkButton";
import { setMiniNavbar } from "hooks/context";
import { setMapDevices } from "hooks/context";
import UptimeBarCard from "components/Cards/UptimeBarCard";

function getGroupData(group_fetch_time, devices_fetch_time, devices_data, group_data, show_inactive = false) {
    var data = {};
    var showing_all = true;

    if (group_data != null) {
        if (group_data.length != 0) {
            showing_all = false;
        }
    }

    if (showing_all) {
        var data = devices_data;
        data.name = "All Devices";
        data.devices = processDevicesData(devices_fetch_time, devices_data, show_inactive);
    }
    else {
        data = processGroupData(group_fetch_time, group_data, devices_data, show_inactive);
    }

    data.showing_all = showing_all;

    return data;
}

function CustomTabPanel(props) {
    const { children, value, index, noHeight = false, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {(value === index && noHeight) && <Box>{children}</Box>}
            {(value === index && !noHeight) && <Box sx={{ height: '100%' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function ClearwaysSidebar({ user, groupPreviewID = 0, mapOptions = [], events, devices, selectedDevice, setSelectedDevice }) {
    const [controller, dispatch] = useSoftUIController();
    const { deviceLayout, miniNavbar, mapDevices, selectedOrg, userPermissions } = controller;
    const navigate = useNavigate();

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;
    const accessUserLocation = userPermissions.indexOf('misc:user-location') >= 0;

    const showMapOptions = mapOptions.length > 0;

    const [header, subheader, triheader] = getNamingConventions(selectedDevice?.name || "");
    const [currentLocation, setCurrentLocation] = useState([0, 0]);

    useEffect(() => {
        if (accessUserLocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setCurrentLocation([latitude, longitude]);
                },
                (error) => {
                    console.error("Error detecting location:", error);
                    if (error.code === error.TIMEOUT) {
                        console.warn("Location request timed out. Setting to 0,0.");
                    }
                    setCurrentLocation([0, 0]);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000,
                    maximumAge: 0
                }
            );
        }
    }, [user, accessUserLocation]);

    useEffect(() => {
        if (deviceLayout == 'desktop') {
            setMiniNavbar(dispatch, false);
        }
        else {
            setMiniNavbar(dispatch, true);
        }
    }, [deviceLayout]);

    useEffect(() => {
        if (miniNavbar && !mapDevices) {
            setSelectedDevice(null);
        }
    }, [miniNavbar]);

    const [tabValue, setTabValue] = useState(0);

    const handleTabClick = (event, newValue) => {
        setTabValue(newValue);
    };

    groupAPI.loadToken(useAuth0());

    const groups = useQuery({
        queryKey: ['getAllGroups', selectedOrg],
        queryFn: async () => {
            return await groupAPI.getAllGroups();
        }
    });

    const group = useQuery({
        queryKey: ['getGroupWithID', groupPreviewID, selectedOrg],
        queryFn: async () => {
            return await groupAPI.getGroupWithID(groupPreviewID);
        }
    });

    if (groups.isLoading) {
        return ('Loading...');
    }

    if (groups.error) {
        return ("An error has occurred");
    }

    var group_data = getGroupData(group.dataUpdatedAt, devices.dataUpdatedAt, devices.data, group.data);
    var position = { latitude: 0, longitude: 0 };

    try {
        var lat = devices.data.map((element) => element.position.latitude);
        position.latitude = lat.reduce((x, y) => {
            return x + y;
        }, 0) / devices.data.length;

        var lng = devices.data.map((element) => element.position.longitude);
        position.longitude = lng.reduce((x, y) => {
            return x + y;
        }, 0) / devices.data.length;

        if (isNaN(position.latitude) || isNaN(position.longitude)) {
            var position = { latitude: 33.8688, longitude: 151.2093 };
        }
    }
    catch (e) {
        console.log('improper device data provided');
    }

    const colours = ['success', 'warning', 'error'];
    const renderBadgeDots = ['Full', 'Healthy', 'Low'].map((label, index) => {
        return (
            <SoftBadgeDot
                key={`badge-dot-${index}`}
                variant="gradient"
                color={colours[index]}
                size="lg"
                badgeContent={label}
                font={{ color: "text", weight: "medium" }}
                px={0}
            />
        );
    });

    var liveview_cards = groups.data.filter(row => (groupPreviewID == 0 || row.parent_id == groupPreviewID || row.group_id == groupPreviewID) && row.type == "clearways").map(function (row) {
        var ret = processGroupData(group.dataUpdatedAt, row, devices.data);
        return ret;
    }).sort((a, b) => {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    }).map(function (row) {
        if (row.devices.length > 0) {
            return (
                <Grid item key={row.group_id} xxs={12} md={6} xl={12}>
                    <Card sx={{ paddingBottom: '12px', height: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
                        <Grid container>
                            <Grid item xxs={12}>
                                <SoftBox sx={{ height: '100%', width: '100%' }} >
                                    <SoftBox sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                                        <GroupLiveView group={row} />
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                            <Grid item xxs={12}>
                                <SoftBox pb={1.5} sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', textAlign: 'center' }}>
                                    <SoftTypography variant='h5' textTransform='capitalize' fontWeight='bold'>
                                        {row.name}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            );
        }
    }).filter(row => typeof row != 'undefined');

    const maxWidth = '450px';
    const ignoreMiniNavbar = true;
    const backgroundEnabled = false;

    return (
        <SoftBox sx={{ height: '100%', paddingBottom: '8px' }}>
            {sidebarWrapper((
                <>
                    <SoftBox mb={1} sx={({ palette: { white }, functions: { rgba } }) => ({ borderRadius: '16px', backgroundColor: rgba(white.main, 0.7), transition: 'height 200ms ease-in-out' })}>
                        <SoftBox p={1}>
                            <Grid container spacing={1}>
                                {deviceLayout == 'mobile' && (
                                    <Grid item xxs={6}>
                                        <SoftButton disabled={mapDevices} fullWidth onClick={() => setMiniNavbar(dispatch, !miniNavbar)} sx={{ p: 0, minHeight: '36px !important' }}>Toggle Menu</SoftButton>
                                    </Grid>
                                )}
                                <Grid item xxs={deviceLayout == 'mobile' ? 6 : 12}>
                                    <SoftBox
                                        display="flex"
                                        flex={1}
                                        flexDirection="row"
                                        justifyContent="center"
                                        alignItems="center"
                                        ml={deviceLayout == 'mobile' ? 0.5 : 0}
                                        p={0.25}
                                        gap={0.5} // Adds uniform spacing between the two buttons
                                        sx={{
                                            backgroundColor: "#eceff1",
                                            borderRadius: "0.5rem",
                                            minHeight: '24px',
                                            border: "1px solid #ccc"
                                        }}
                                    >
                                        {["groups", "devices"].map((type) => {
                                            let selected = (mapDevices && type == "devices") || (!mapDevices && type == "groups")

                                            return (
                                                <SoftButton
                                                    key={type}
                                                    variant={selected ? "gradient" : "text"}
                                                    color={"secondary"}
                                                    size="small"
                                                    sx={{
                                                        p: '0.5rem 0rem !important',
                                                        minWidth: '40px !important',
                                                        flex: 1, // Forces both buttons to take exactly 50% of the container
                                                        borderRadius: "0.5rem",
                                                        textTransform: "capitalize",
                                                        height: 'auto',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent'
                                                        },
                                                        '&:focus:not(:hover)': {
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }}
                                                    onClick={() => setMapDevices(dispatch, !mapDevices)}
                                                >
                                                    {type.replace("-", " ")}
                                                </SoftButton>
                                            )
                                        })}
                                    </SoftBox>
                                </Grid>
                            </Grid>
                        </SoftBox>
                    </SoftBox>
                    {!mapDevices && (
                        <SoftBox display={!miniNavbar ? "block" : "none"} height={(deviceLayout == "mobile" ? "calc(100svh - 9.75rem)" : "calc(100svh - 11.75rem)")} p={1} sx={({ palette: { white }, functions: { rgba } }) => ({ borderRadius: '16px', overflow: 'hidden', backgroundColor: rgba(white.main, 0.7), transition: 'height 200ms ease-in-out' })}>
                            <Grid container display={!miniNavbar && !mapDevices ? 'flex' : 'none'}>
                                <Grid item xxs={12} sm={12} mb={1} sx={{ width: 'calc(100% - 16px)' }}>
                                    <Tabs value={tabValue}
                                        onChange={handleTabClick} aria-label="ClearwaysSidebar Tabs"
                                        allowScrollButtonsMobile
                                        variant="scrollable"
                                        scrollButtons
                                        sx={{
                                            display: !miniNavbar && !mapDevices ? 'flex' : 'none',
                                            opacity: 0.8,
                                            [`& .${tabsClasses.scrollButtons}`]: {
                                                '&.Mui-disabled': { opacity: 0 },
                                            },
                                            '& .MuiTabs-indicator': {
                                                top: '3px',
                                                left: tabValue == 0 ? '1px !important' : '0px',
                                                height: 'calc(100% - 6px)'
                                            }
                                        }}
                                    >
                                        <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Liveviews" {...a11yProps(0)} />
                                        <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Statistics" {...a11yProps(1)} />
                                        <Tab sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label="Upcoming Events" {...a11yProps(2)} />
                                    </Tabs>
                                </Grid>
                            </Grid>
                            <CustomTabPanel value={tabValue} noHeight index={0}>
                                <SoftBox height={deviceLayout == "mobile" ? "calc(100svh - 13.5rem)" : "calc(100svh - 15.5rem)"}>
                                    <Grid container sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
                                        <Grid item xxs={12} sx={(theme) => exploreContentStyle(theme, { deviceLayout, miniNavbar })}>
                                            <SoftBox sx={{ borderRadius: '12px', overflowY: 'auto' }}>
                                                {liveview_cards.length > 0 && (
                                                    <SoftBox sx={{ height: '100%' }}>
                                                        <Grid container spacing={1}>
                                                            {liveview_cards}
                                                        </Grid>
                                                    </SoftBox>
                                                )}
                                                {liveview_cards.length == 0 && (
                                                    <SoftBox display='flex' flexDirection='column' justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
                                                        <SoftTypography variant={window.innerWidth < breakpoints.up("sm") ? "h6" : "h5"}>
                                                            No Previews Available!
                                                        </SoftTypography>
                                                    </SoftBox>
                                                )}
                                            </SoftBox>
                                        </Grid>
                                    </Grid>
                                </SoftBox>
                            </CustomTabPanel>
                            <CustomTabPanel value={tabValue} noHeight index={1}>
                                <SoftBox height={deviceLayout == "mobile" ? "calc(100svh - 13.5rem)" : "calc(100svh - 15.5rem)"} sx={{ overflowY: 'auto', borderRadius: '12px' }}>
                                    <Grid container>
                                        <Grid item xxs={12} sx={(theme) => exploreContentStyle(theme, { deviceLayout, miniNavbar })}>
                                            <SoftBox sx={{ overflowY: 'auto' }}>
                                                <Grid container spacing={1}>
                                                    <Grid item xxs={12}>
                                                        <UptimeBarCard raw={false} fetch_time={devices.dataUpdatedAt} devices={group_data.showing_all ? group_data : group_data.devices} />
                                                    </Grid>
                                                    <Grid item xxs={12}>
                                                        <Card>
                                                            <SoftBox display="flex" flexDirection="row" justifyContent={'space-evenly'}>
                                                                {renderBadgeDots}
                                                            </SoftBox>
                                                        </Card>
                                                    </Grid>
                                                    <Grid item xxs={12} md={6} xl={12}>
                                                        <StatsPieChart
                                                            title="Battery Levels"
                                                            devices={group_data.devices}
                                                            data_type={"battery"}
                                                        />
                                                    </Grid>
                                                    <Grid item xxs={12} md={6} xl={12}>
                                                        <StatsPieChart
                                                            title="Signal Levels"
                                                            devices={group_data.devices}
                                                            data_type={"rssi"}
                                                        />
                                                    </Grid>
                                                    <Grid item xxs={12} md={6} xl={12}>
                                                        <StatsPieChart
                                                            title="Temperature Levels"
                                                            devices={group_data.devices}
                                                            data_type={"temperature"}
                                                        />
                                                    </Grid>
                                                    <Grid item xxs={12} md={6} xl={12}>
                                                        <StatsPieChart
                                                            title="Humidity Levels"
                                                            devices={group_data.devices}
                                                            data_type={"humidity"}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </SoftBox>
                                        </Grid>
                                    </Grid>
                                </SoftBox>
                            </CustomTabPanel>
                            <CustomTabPanel value={tabValue} noHeight index={2}>
                                <SoftBox height={deviceLayout == "mobile" ? "calc(100svh - 13.5rem)" : "calc(100svh - 15.5rem)"}>
                                    <Grid container sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
                                        <Grid item xxs={12} sx={(theme) => exploreContentStyle(theme, { deviceLayout, miniNavbar })}>
                                            <EventsBar events_data={events} />
                                        </Grid>
                                    </Grid>
                                </SoftBox>
                            </CustomTabPanel>
                        </SoftBox>
                    )}
                    {(mapDevices && selectedDevice != null) && (
                        <SoftBox height={(deviceLayout == "mobile" ? "calc(100svh - 9.75rem)" : "calc(100svh - 11.75rem)")} p={1} sx={({ palette: { white }, functions: { rgba } }) => ({ borderRadius: '16px', overflow: 'hidden', backgroundColor: rgba(white.main, 0.7), transition: 'height 200ms ease-in-out' })}>
                            <Card sx={{ height: "100%", background: '#f9f9f9' }}>
                                <SoftBox py={2} px={2} sx={{ overflowY: 'auto' }}>
                                    <SoftBox
                                        mt={deviceLayout == "mobile" ? 3.5 : 1}
                                        mx={1}
                                        display="flex"
                                        flexDirection="row"
                                        flexWrap="wrap"
                                        alignItems="baseline"
                                        sx={{ gap: 0.5 }}
                                    >
                                        <SoftTypography variant={deviceLayout == "mobile" ? "h5" : "h4"} fontWeight="bold" sx={{ color: "#333", mr: 0.5, flexShrink: 1, width: 'fit-content' }}>{header}</SoftTypography>
                                        {subheader && <SoftTypography variant={deviceLayout == "mobile" ? "subtitle3" : "subtitle2"} fontWeight="bold" sx={{ color: "#777", minWidth: "fit-content" }}>{subheader}{triheader && ', '}</SoftTypography>}
                                        {triheader && <SoftTypography variant={deviceLayout == "mobile" ? "subtitle3" : "subtitle2"} fontWeight="bold" sx={{ color: "#777", minWidth: "fit-content" }}>{triheader}</SoftTypography>}
                                        {deviceLayout == 'mobile' && (
                                            <>
                                                <SoftBox
                                                    sx={{
                                                        position: 'absolute',
                                                        right: '5px',
                                                        top: '5px',
                                                        verticalAlign: 'center',
                                                        alignItems: 'center',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#ddd',
                                                        p: '5px',
                                                        borderRadius: '50px',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={((e) => setSelectedDevice(null))}
                                                >
                                                    <Close fontSize="small" />
                                                </SoftBox>
                                                {accessUserLocation && (
                                                    <SoftBox
                                                        sx={{
                                                            position: 'absolute',
                                                            left: '5px',
                                                            top: '5px',
                                                            verticalAlign: 'center',
                                                            alignItems: 'center',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#ddd',
                                                            borderRadius: '50px'
                                                        }}
                                                    >
                                                        <MapLinkButton locations={{ user_loc: currentLocation, dest_loc: selectedDevice.position }} backgroundColor={'#ddd'} dimensions={"30px"} />
                                                    </SoftBox>
                                                )}
                                            </>
                                        )}
                                    </SoftBox>
                                    <SoftBox my={1}>
                                        <Divider />
                                    </SoftBox>
                                    <SoftBox py={selectedDevice?.inspection_record_id ? 1 : 2} px={selectedDevice?.inspection_record_id ? 0 : 1}>
                                        {selectedDevice?.inspection_record_id ? (
                                            <InspectionLiveviewCard device={selectedDevice} />
                                        ) : (
                                            <>
                                                {selectedDevice.hardware_type == 'bk-camera' ? (
                                                    <CameraLatest uuid={selectedDevice.uuid} />
                                                ) : (
                                                    <SoftBox sx={{ maxHeight: '450px', overflowX: 'hidden' }}>
                                                        <LiveView device={selectedDevice} isPreview={true} minHeight={"450px"} nexus_id={user.organisations.find((org) => org.organisation_id == selectedDevice.organisation_id).nexus_id || null} isPerspectiveLiveview={selectedDevice?.overlay_id && selectedDevice.overlay_id != 'no_frame' ? true : false} />
                                                    </SoftBox>
                                                )}
                                            </>
                                        )}
                                    </SoftBox>
                                    {deviceLayout == 'mobile' && (
                                        <>
                                            <SoftBox my={1}>
                                                <Divider />
                                            </SoftBox>
                                            {selectedDevice.inspection_record_id ? (
                                                <SoftBox display="flex" gap={0.5}>
                                                    <SoftButton component={Link} to={`../../operations/assignments/${selectedDevice.inspection_record_id}`} variant="contained" color="dark" size="small" fullWidth sx={{ padding: '0.5rem !important' }}>
                                                        Complete Assignment
                                                    </SoftButton>
                                                </SoftBox>
                                            ) : (
                                                <SoftBox display="flex" gap={0.5}>
                                                    <SoftButton component={Link} to="/operations/inspections/new" state={{ currentDevice: selectedDevice }} variant="contained" color="dark" size="small" fullWidth sx={{ padding: '0.5rem !important' }}>
                                                        New Inspection
                                                    </SoftButton>
                                                    <SoftButton component={Link} to={`../devices/${selectedDevice.uuid}`} variant="contained" color="light" size="small" fullWidth sx={{ padding: '0.5rem !important' }}>
                                                        View Device Details
                                                    </SoftButton>
                                                </SoftBox>
                                            )}
                                        </>
                                    )}
                                    <SoftBox my={1}>
                                        <Divider />
                                    </SoftBox>
                                    <Card>
                                        <SoftBox mx={1}>
                                            <HealthStatusCard statistics={selectedDevice} variant={deviceLayout == "mobile" ? "d6" : "d5"} showAdvancedDeviceLevel={showAdvancedDeviceLevel} transparent={true} />
                                            {!selectedDevice.removed && (
                                                <>
                                                    {(typeof selectedDevice?.battery != 'undefined' && typeof selectedDevice?.temperature != 'undefined') && (
                                                        <>
                                                            <SoftBox my={0.5}>
                                                                <Divider />
                                                            </SoftBox>
                                                            <Grid container>
                                                                <Grid item xxs={12} x4l={6}>
                                                                    <StatsCounterCard
                                                                        count={selectedDevice.battery}
                                                                        suffix="%"
                                                                        title="Battery"
                                                                        description=""
                                                                        transparent={true}
                                                                    />
                                                                </Grid>
                                                                <Box sx={({ breakpoints }) => ({
                                                                    width: 0, py: 1, display: "flex", justifyContent: "center",
                                                                    opacity: 0,
                                                                    [breakpoints.up("x4l")]: {
                                                                        opacity: 1,
                                                                    }
                                                                })}>
                                                                    <Divider orientation="vertical" flexItem />
                                                                </Box>
                                                                <Grid item xxs={12}
                                                                    sx={({ breakpoints }) => ({
                                                                        [breakpoints.up("x4l")]: {
                                                                            display: 'none',
                                                                        }
                                                                    })}>
                                                                    <Divider orientation="horizontal" />
                                                                </Grid>
                                                                <Grid item xxs={12} x4l={6}>
                                                                    <StatsCounterCard
                                                                        count={selectedDevice.temperature}
                                                                        suffix={<>&deg;C</>}
                                                                        title="Temperature"
                                                                        description=""
                                                                        transparent={true}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    <SoftBox my={0.5}>
                                                        <Divider />
                                                    </SoftBox>
                                                    <DeviceStatsCard icon={getConnectivityIcon(selectedDevice, showAdvancedDeviceLevel)} transparent={true} variant="h6" title={{ text: getConnectivityIcon(selectedDevice, showAdvancedDeviceLevel).text, fontWeight: 'medium' }} />
                                                    <SoftBox my={0.5}>
                                                        <Divider />
                                                    </SoftBox>
                                                    <DeviceStatsCard icon={getDeviceTypeIcon(selectedDevice, showAdvancedDeviceLevel)} transparent={true} variant="h6" title={{ text: getDeviceTypeIcon(selectedDevice, showAdvancedDeviceLevel).text, fontWeight: 'medium' }} />
                                                </>
                                            )}
                                        </SoftBox>
                                    </Card>
                                </SoftBox>
                            </Card>
                        </SoftBox>
                    )}
                </>
            ), controller, selectedDevice != null, maxWidth, ignoreMiniNavbar, backgroundEnabled)
            }
        </SoftBox >
    );
}

ClearwaysSidebar.propTypes = {
    user: PropTypes.object,
    groupPreviewID: PropTypes.number || PropTypes.string,
    events: PropTypes.array
}

export default ClearwaysSidebar;
